// Toast notification system (configurable via theme settings)
// Reads window.toastConfig + CSS variables injected in Liquid.

class ToastManager {
  constructor() {
    /** @type {{position:string,duration:number}} */
    this.config = Object.assign({
      position: 'top-right',
      duration: 4000
    }, window.toastConfig || {});
    this.ensureContainer();
    this.bindGlobalEvents();
  }

  ensureContainer() {
    if (this.container) return;
    this.container = document.createElement('div');
    this.container.className = 'toast-container';
    this.container.setAttribute('data-position', this.config.position);
    document.body.appendChild(this.container);
  }

  bindGlobalEvents() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.clearAll();
      }
    });
  }

  /** @param {string} position */
  setPosition(position) {
    this.config.position = position;
    if (this.container) {
      this.container.setAttribute('data-position', position);
    }
  }

  /**
   * @param {string} message
   * @param {{type?:string,duration?:number,icon?:string|null,persistent?:boolean}} [options]
   */
  show(message, { type = 'info', duration, icon = null, persistent = false } = {}) {
    if (!message) return;
    this.ensureContainer();
    const container = this.container;
    if (!container) return;

    const toast = document.createElement('div');
    toast.className = 'toast';
    if (type) toast.classList.add(`--${type}`);

    if (icon) {
      const iconWrapper = document.createElement('span');
      iconWrapper.className = 'toast__icon';
      iconWrapper.innerHTML = icon; // icon should be safe SVG string
      toast.appendChild(iconWrapper);
    }

    const textWrapper = document.createElement('span');
    textWrapper.className = 'toast__message';
    textWrapper.textContent = message;
    toast.appendChild(textWrapper);

    container.appendChild(toast);

    // Force layout then animate in
    requestAnimationFrame(() => {
      toast.classList.add('--visible');
    });

    const hideAfter = typeof duration === 'number' ? duration : this.config.duration * 1000;
    if (!persistent) {
      setTimeout(() => this.hide(toast), hideAfter);
    }

    return toast;
  }

  /** @param {HTMLElement} toast */
  hide(toast) {
    if (!toast) return;
    toast.classList.remove('--visible');
    setTimeout(() => {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 300);
  }

  clearAll() {
    if (!this.container) return;
    Array.from(this.container.querySelectorAll('.toast')).forEach(t => this.hide(/** @type {HTMLElement} */(t)));
  }
}

// Initialize singleton
window.toast = new ToastManager();

// Optional API alias
window.showToast = (message, opts) => window.toast && window.toast.show(message, opts);
